import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, formatApiErrorDetail, getToken, setToken } from "@/lib/api";
import { unlockAndCachePiiKey, clearPiiKey } from "@/lib/profileCrypto";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [checked, setChecked] = useState(false);

  const refresh = useCallback(async () => {
    // If returning from OAuth callback, defer to AuthCallback to set the token first
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      setChecked(true);
      return;
    }
    if (!getToken()) {
      setUser(false);
      setChecked(true);
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      setToken(null);
      setUser(false);
    } finally {
      setChecked(true);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const login = async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setToken(data.access_token);
      setUser(data.user);
      // Phase 3 Layer B — derive the per-user PII key from the password
      // and cache it in sessionStorage. PBKDF2 takes ~200ms; non-blocking
      // ok since we already have the user logged in by this point.
      try { await unlockAndCachePiiKey({ password, userId: data.user?.id }); } catch { /* noop */ }
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  };

  const register = async (name, email, password, extras = {}) => {
    try {
      const { data } = await api.post("/auth/register", { name, email, password, ...extras });
      setToken(data.access_token);
      setUser(data.user);
      try { await unlockAndCachePiiKey({ password, userId: data.user?.id }); } catch { /* noop */ }
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch { /* ignore */ }
    setToken(null);
    setUser(false);
    clearPiiKey();
    // Clear selected-board so the next user doesn't inherit our context
    try { localStorage.removeItem("sonspeak_selected_board_v1"); } catch { /* noop */ }
  };

  const setRole = async (role) => {
    try {
      const { data } = await api.put("/auth/role", { role });
      setUser(data);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  };

  return (
    <AuthContext.Provider value={{ user, checked, login, register, logout, refresh, setRole }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
